import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { CSRF_TOKEN } from "@/config";

let csrfToken = CSRF_TOKEN;

// Set by AuthProvider so a dead session drops the reviewer on the login screen
// instead of leaving a page up that can't do anything.
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

// One replay per request, so a server that keeps rejecting the token can't put
// us in a loop.
type ReplayableConfig = InternalAxiosRequestConfig & { csrfReplayed?: boolean };

export const api = axios.create({
  baseURL: "/api/v1",
  withCredentials: true,
  headers: { "X-Requested-With": "XMLHttpRequest" },
});

api.interceptors.request.use((config) => {
  config.headers.set("X-CSRF-Token", csrfToken);
  return config;
});

function absorbCsrfToken(response: AxiosResponse): void {
  const token = response.headers["x-csrf-token"];
  if (typeof token === "string" && token.length > 0) {
    csrfToken = token;
  }
}

api.interceptors.response.use(
  (response) => {
    absorbCsrfToken(response);
    return response;
  },
  async (error: AxiosError<{ code?: string }>) => {
    const response = error.response;
    if (!response) return Promise.reject(error);
    // Error responses carry a fresh token too, notably the one below.
    absorbCsrfToken(response);

    const config = error.config as ReplayableConfig | undefined;
    const staleToken = response.status === 401 && response.data?.code === "invalid_csrf_token";
    // The token we sent was stale (the session expired under an open tab). We
    // just picked up a good one, so replay rather than showing the reviewer an
    // error they can't act on. If the session really is gone the replay comes
    // back a plain 401 and falls through to the login bounce below.
    if (staleToken && config && !config.csrfReplayed) {
      config.csrfReplayed = true;
      return api(config);
    }

    if (response.status === 401) onUnauthorized?.();
    return Promise.reject(error);
  },
);
